import { createApiError } from "../../../utilities/apiError";
import { PeoplePickerUser } from "../../../components/components/fileViewer/pdfViewer/editorComponents/SignatureBoxesPanel";
import { SignatureBox } from "../../../interfaces/file/fileSignature/SignatureBox";
import { CreateSignatureRequestPayload, SignatureRequestResponse } from "../../../interfaces/file/fileSignature/SignatureCreationRequests";
import { AllSignatureRequestResDto, EmployeeCompletedSignatureHistoryItem, EmployeeSignatureHistorySubject, SignatureRequest, SignatureRequestDetails, SignatureRequestQueryReq } from "../../../interfaces/file/fileSignature/SignatureRequest";
import { ApiResponse, ServiceOperationResult } from "../../../interfaces/genericResponse/GenericResponse";
import { PagedResult, SignatureRequestDto } from "../../../interfaces/signature/SignatureRequestDto";
import { SignatureRequestQueryDto } from "../../../interfaces/signature/SignatureRequestQueryDto";
import { apiGet } from "../../../utilities/api-client/apiRequest";
import { buildQuery } from "../../../utilities/common/buildQueryParams";

// Creates a signature request and returns the created record
export async function createSignatureRequest(
  payload: CreateSignatureRequestPayload,
  authToken: string
): Promise<ServiceOperationResult<SignatureRequestResponse>> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/generate-request`;

  console.log("Request Payload", payload);
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      // Try to parse structured error response
      let errorData: any;
      try {
        errorData = await response.json();
      } catch {
        errorData = null;
      }

      return {
        success: false,
        message: errorData?.message || errorData?.error || "Failed to create signature request",
        errorCode: errorData?.errorCode,
        taskDisplayId: errorData?.taskDisplayId,
        statusCode: response.status,
      };
    }

    const data = await response.json();
    return {
      success: true,
      data: data,
    };
  } catch (error: any) {
    console.error("Error creating signature request", error);
    return {
      success: false,
      message: error?.message || "Failed to create signature request",
      statusCode: 500,
    };
  }
}

export async function fetchSignatureRequestByToken(
  token: string
): Promise<SignatureRequest> {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}`,
      {
        method: "GET",
      }
    );
    if (!res.ok) {
      const responseText = await res.text();
      let message = "This signing link is invalid or no longer available.";
      try {
        const errorBody = JSON.parse(responseText);
        message = errorBody?.message || errorBody?.Message || message;
      } catch {
        if (responseText) message = responseText;
      }
      throw new Error(`${message} (HTTP ${res.status})`);
    }

    const response = await res.json();
    return response;
  } catch (err) {
    console.error("Unable to load public signature request:", err);
    throw err;
  }
}

export async function fillSignatureBox(
  token: string,
  boxId: string,
  signatureValue: string
): Promise<boolean> {
  const res = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}/boxes/${boxId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ signatureValue, electronicConsentAccepted: true, consentVersion: "esign-consent-v1" }),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body?.message || body?.error || "Unable to sign this field.") as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  return true;
}

export async function fetchStoredSignatureImage(
  token: string,
  boxId: string
): Promise<string | null> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}/boxes/${boxId}/signature`,
      { method: "GET", cache: "no-store" }
    );
    if (!response.ok) return null;
    return URL.createObjectURL(await response.blob());
  } catch (error) {
    console.error("Unable to load stored signature image:", error);
    return null;
  }
}

export interface SavedEsignature {
  signatureValue: string;
  initials: string;
  signatureKind: "typed" | "drawn";
  updatedAt: string;
}

const authenticatedHeaders = (idToken: string, json = false) => ({
  Authorization: `Bearer ${idToken}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

export async function fetchAuthenticatedSignatureRequest(token: string, idToken: string): Promise<SignatureRequest> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/authenticated/${token}`, {
    headers: authenticatedHeaders(idToken),
    cache: "no-store",
  });
  if (!response.ok) throw new Error((await response.json().catch(() => ({})))?.message || "This signature request is unavailable.");
  return response.json();
}

export async function fillAuthenticatedSignatureBox(token: string, boxId: string, signatureValue: string, idToken: string): Promise<boolean> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/authenticated/${token}/boxes/${boxId}`, {
    method: "PUT",
    headers: authenticatedHeaders(idToken, true),
    body: JSON.stringify({ signatureValue, electronicConsentAccepted: true, consentVersion: "esign-consent-v1" }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body?.message || body?.error || "Unable to sign this field.") as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  return true;
}

export async function fetchAuthenticatedStoredSignatureImage(token: string, boxId: string, idToken: string): Promise<string | null> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/authenticated/${token}/boxes/${boxId}/signature`, {
    headers: authenticatedHeaders(idToken),
    cache: "no-store",
  });
  return response.ok ? URL.createObjectURL(await response.blob()) : null;
}

export async function getPendingSignatureInboxCount(idToken: string, signal?: AbortSignal): Promise<number> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/signature-inbox/pending-count`, {
    headers: authenticatedHeaders(idToken),
    cache: "no-store",
    signal,
  });
  if (!response.ok) throw new Error("Unable to load pending signature count.");
  return Number((await response.json()).count || 0);
}

export async function getSavedEsignature(idToken: string): Promise<SavedEsignature | null> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/saved-signature`, {
    headers: authenticatedHeaders(idToken),
    cache: "no-store",
  });
  if (response.status === 204) return null;
  if (!response.ok) throw new Error("Unable to load your saved signature.");
  return response.json();
}

export async function saveEsignature(idToken: string, signatureValue: string, initials: string): Promise<SavedEsignature> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/saved-signature`, {
    method: "PUT",
    headers: authenticatedHeaders(idToken, true),
    body: JSON.stringify({ signatureValue, initials, saveConsent: true }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => ({})))?.error || "Unable to save your signature.");
  return {
    signatureValue,
    initials,
    signatureKind: signatureValue.startsWith("data:image/png;base64,") ? "drawn" : "typed",
    updatedAt: new Date().toISOString(),
  };
}

export async function deleteSavedEsignature(idToken: string): Promise<void> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sign/saved-signature`, {
    method: "DELETE",
    headers: authenticatedHeaders(idToken),
  });
  if (!response.ok) throw new Error("Unable to remove your saved signature.");
}

/**
 * Submits a completed signature request.
 * This endpoint should only be called once all signature boxes have been signed.
 * @param token The signature request token.
 * @returns True if submission was successful; false otherwise.
 */
export async function submitSignatureRequest(
  token: string
): Promise<{ finalizedPdfUrl: string; signingLogUrl: string } | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/submit/${token}`;
    // We assume no additional body data is needed.
    const response = await fetch(apiUrl, {
      method: "PUT",
    });
    if (response.status === 200) {
      return response.json();
    } else {
      const responseValue = await response.text();
      console.log(responseValue);
    }
    return null;
  } catch (error) {
    console.error("Error submitting signature request", error);
    return null;
  }
}

export async function searchEmails(
  query: string,
  token: string,
  employeesOnly = false,
  employeeFileId?: string,
): Promise<PeoplePickerUser[]> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL
      }/sign/search?query=${encodeURIComponent(query)}${employeesOnly ? `&employeesOnly=true&employeeFileId=${encodeURIComponent(employeeFileId || "")}` : ""}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const result = await response.json();
      // Assuming the API returns: [{ email: "test@example.com" }, ...]
      // return result.map((item: { email: string }) => item.email);
      return await result;
    } else {
      const errorText = await response.text();
      console.log("Search failed:", errorText);
    }

    return [];
  } catch (error) {
    console.error("Error searching emails:", error);
    return [];
  }
}

export async function getPendingSignatureRequests(
  authToken: string,
  queryDto: SignatureRequestQueryDto
): Promise<PagedResult<SignatureRequestDto> | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/SignaturePendingRequest`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(queryDto),
    });

    if (!response.ok) {
      console.error(
        "Failed to fetch signature requests",
        await response.json()
      );
      return null;
    }

    const result: PagedResult<SignatureRequestDto> = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching signature requests", error);
    return null;
  }
}

export async function resendSignatureRequest(
  authToken: string,
  requestId: string
): Promise<boolean> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/resendSignature-request?fileId=${requestId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw createApiError({
        status: response.status,
        message: body?.error || "Failed to resend signature request",
        details: body,
      });
    }

    return true;
  } catch (error) {
    console.error("Error resending signature request", error);
    throw error;
  }
}

export async function getSignatureBoxes(
  authToken: string,
  fileId: string,
  ownerId: string,
  sourceType: "case-file" | "employee-file" = "case-file"
): Promise<SignatureBox[] | null> {
  const apiUrl = sourceType === "employee-file"
    ? `${process.env.REACT_APP_API_BASE_URL}/sign/employee-file/${encodeURIComponent(fileId)}/boxes`
    : `${process.env.REACT_APP_API_BASE_URL}/sign/signature-boxes?fileId=${fileId}&ownerId=${ownerId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch signature boxes");
      return null;
    }

    const data = await response.json();
    return data as SignatureBox[];
  } catch (error) {
    console.error("Error fetching signature boxes", error);
    return null;
  }
}

export async function canRequestEmployeeSignature(authToken: string, fileId: string): Promise<boolean> {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/sign/employee-file/${encodeURIComponent(fileId)}/eligibility`,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  if (!response.ok) return false;
  const result = await response.json();
  return result.canRequest === true;
}

export async function downloadCompletedEmployeeSignature(
  authToken: string,
  requestId: string,
): Promise<Blob> {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/sign/employee-file/completed/${encodeURIComponent(requestId)}/download`,
    { headers: authenticatedHeaders(authToken) },
  );
  if (!response.ok) throw new Error("Your completed signature copy is unavailable.");
  return response.blob();
}

export async function resendEmployeeSignatureRequest(authToken: string, fileId: string): Promise<boolean> {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/sign/employee-file/${encodeURIComponent(fileId)}/resend`,
    { method: "POST", headers: authenticatedHeaders(authToken) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || "Failed to resend employee signature request.");
  }
  return true;
}

export async function retryEmployeeSignatureFinalization(authToken: string, fileId: string): Promise<void> {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/sign/employee-file/${encodeURIComponent(fileId)}/finalize`,
    { method: "POST", headers: authenticatedHeaders(authToken) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || "Unable to finalize the employee signing package.");
  }
}

export async function submitSignature(
  authToken: string,
  fileId: string,
  ownerId: string
): Promise<any | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/submit?fileId=${fileId}&ownerId=${ownerId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.error("Failed to submit signature request");
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error submitting signature request", error);
    return null;
  }
}

export async function submitSignatureCancelReq(
  authToken: string,
  fileId: string,
  ownerId: string,
  status: string = "completed" // optional param
): Promise<ApiResponse<string>> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/update-status`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileId,
        ownerId,
        status,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

export async function cancelSignatureRequestById(
  authToken: string,
  signBoxId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/cancel-by-request-id/${signBoxId}`;
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    const responseText = await response.text();

    if (response.status === 200) {
      return { success: true, message: responseText };
    } else if (response.status === 404) {
      return { success: false, message: `Not Found: ${responseText}` };
    } else {
      return { success: false, message: `Server Error: ${responseText}` };
    }
  } catch (error: any) {
    console.error("Error cancelling signature request", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function deleteSignatureBoxById(
  authToken: string,
  boxId: string
): Promise<{ success: boolean; message?: string }> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/box/${boxId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json(); // Assuming your API returns the ApiResponse object

    if (response.ok) {
      return { success: true, message: data.message };
    } else {
      return { success: false, message: data.message || "Failed to delete box" };
    }
  } catch (error) {
    console.error("Error in deleteSignatureBoxById service:", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function deleteSignatureRequest(
  authToken: string,
  boxId: string
): Promise<{ success: boolean; message?: string }> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/box/${boxId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || `Failed to delete signature request: ${response.statusText}`
      };
    }

    const data = await response.json().catch(() => ({ message: "Deleted successfully" }));
    return { success: true, message: data.message || "Signature request deleted successfully" };
  } catch (error) {
    console.error("Error in deleteSignatureRequest service:", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function getSignatureRequestsForFile(
  authToken: string,
  fileId: string
): Promise<SignatureRequest[] | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/file/${fileId}`;
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.status === 200) {
      const responseVal = await response.json();
      console.log(responseVal);
      return responseVal;
    } else {
      const responseValue = await response.text();
      console.log(responseValue);
    }
    return null;
  } catch (error) {
    console.error("Error fetching signature requests", error);
    return null;
  }
}

// services/documentService.ts

export async function getAllSignatureRequests(
    token: string,
    payload: SignatureRequestQueryReq,
    signal?: AbortSignal
): Promise<{ items: AllSignatureRequestResDto[]; totalCount: number } | null> {
    const queryParms = buildQuery(payload);
    const apiUrl = `/sign/AllSignatureRequest?${queryParms}`;


    var res = apiGet<{ items: AllSignatureRequestResDto[]; totalCount: number } | null>(
        apiUrl, token, signal
    )
    return res;
}

/** Quick-view metadata; access matches the Inbox/Sent row it was opened from. */
export async function getSignatureRequestDetails(
  token: string,
  requestId: string,
  signal?: AbortSignal
) {
  return apiGet<SignatureRequestDetails>(
    `/sign/requests/${encodeURIComponent(requestId)}/details`,
    token,
    signal
  );
}

export async function getEmployeeSignatureHistorySubjects(
  token: string,
  searchTerm?: string,
  signal?: AbortSignal
) {
  const query = searchTerm ? `?searchTerm=${encodeURIComponent(searchTerm)}` : "";
  return apiGet<EmployeeSignatureHistorySubject[]>(
    `/sign/employee-history/subjects${query}`,
    token,
    signal
  );
}

export async function getCompletedEmployeeSignatureHistory(
  token: string,
  employeeId: string,
  payload: Omit<SignatureRequestQueryReq, "ownerId" | "perspective" | "signStatus" | "participantSearch">,
  signal?: AbortSignal
) {
  const queryParams = buildQuery(payload);
  return apiGet<{
    items: EmployeeCompletedSignatureHistoryItem[];
    totalCount: number;
  }>(
    `/sign/employee-history/${encodeURIComponent(employeeId)}/completed?${queryParams}`,
    token,
    signal
  );
}

export async function getSignatureInboxRequests(
  idToken: string,
  userId: string,
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
  sortColumn?: string,
  sortDirection?: string,
  dateFrom?: string,
  dateTo?: string,
  status?: string,
  requestId?: string,
  signal?: AbortSignal,
): Promise<{ items: any[]; totalCount: number }> {
  const queryParams = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    ...(sortColumn && { sortColumn }),
    ...(sortDirection && { sortDirection }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    ...(status && status !== "All Status" && { status }),
    ...(requestId && { requestId }),
  });

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL
    }/sign/signature-inbox-requests/${userId}?${queryParams.toString()}`;

  const response = await fetch(apiUrl, {
    method: "GET",
    signal,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw createApiError({
      status: response.status,
      message: "Failed to fetch signature inbox requests",
      details: errorBody,
    });
  }

  const result = await response.json();

  // Handle both response structures: direct {items, totalCount} or wrapped {data: {items, totalCount}}
  const items = result.items || result.data?.items || [];
  const totalCount = result.totalCount || result.data?.totalCount || 0;

  if (!Array.isArray(items)) {
    console.error("Expected items to be an array, got:", items);
    return { items: [], totalCount: 0 };
  }

  return {
    items: items,
    totalCount: totalCount,
  };
}
